/**
 * RememberDevice.js - Server-side only implementation
 */

import { ref } from 'vue';

class RememberDevice {
  constructor() {
    this.fingerprintCache = ref(null);
  }



  generateDeviceId() {
    return new Promise((resolve) => {
      this.getDeviceFingerprint().then(fingerprint => {
        const timestamp = Date.now().toString(36);
        const randomStr = Math.random().toString(36).substring(2, 10);
        const combinedData = `${fingerprint}|${timestamp}|${randomStr}`;
        // Simple hash without EnhancedSecurity dependency
        let hash = 0;
        for (let i = 0; i < combinedData.length; i++) {
          const char = combinedData.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        resolve(Math.abs(hash).toString(36));
      });
    });
  }
  getDeviceFingerprint() {
    return new Promise((resolve) => {
      if (this.fingerprintCache.value) {
        resolve(this.fingerprintCache.value);
        return;
      }
      
      // First try to get the fingerprint from localStorage for consistency
      const storageKey = 'device_fingerprint_v2';
      const storedFingerprint = localStorage.getItem(storageKey);
      
      if (storedFingerprint) {
        this.fingerprintCache.value = storedFingerprint;
        resolve(storedFingerprint);
        return;
      }
      
      // If no stored fingerprint, generate a new one
      try {
        // Try the FingerprintJS approach first
        import('@fingerprintjs/fingerprintjs')
          .then(FingerprintJS => FingerprintJS.load())
          .then(fp => fp.get())
          .then(result => {
            const fingerprint = result.visitorId;
            // Store for future use
            localStorage.setItem(storageKey, fingerprint);
            this.fingerprintCache.value = fingerprint;
            resolve(fingerprint);
          })
          .catch(() => {
            // Fallback to basic fingerprint
            const domain = window.location.hostname;
            const userAgent = navigator.userAgent.substring(0, 50);
            const language = navigator.language || '';
            const screenInfo = `${window.screen.width}x${window.screen.height}`;
            const colorDepth = window.screen.colorDepth || '';
            const platform = navigator.platform || '';
            const randomSeed = Math.random().toString(36).substring(2, 10);
            
            // Create a stable fingerprint from these values plus a random seed
            const combinedData = `${domain}|${userAgent}|${language}|${screenInfo}|${colorDepth}|${platform}|${randomSeed}`;
            let hash = 0;
            for (let i = 0; i < combinedData.length; i++) {
              const char = combinedData.charCodeAt(i);
              hash = ((hash << 5) - hash) + char;
              hash = hash & hash;
            }
            const stableFingerprint = Math.abs(hash).toString(36);
            
            // Store for future use
            localStorage.setItem(storageKey, stableFingerprint);
            this.fingerprintCache.value = stableFingerprint;
            resolve(stableFingerprint);
          });
      } catch (error) {
        // Ultimate fallback - generate a random fingerprint and store it
        const fallbackFingerprint = Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
        localStorage.setItem(storageKey, fallbackFingerprint);
        this.fingerprintCache.value = fallbackFingerprint;
        resolve(fallbackFingerprint);
      }
    });
  }
  
  getBrowserName() {
    const userAgent = navigator.userAgent;
    let browserName = "Unknown";
    
    if (userAgent.match(/chrome|chromium|crios/i)) {
      browserName = "Chrome";
    } else if (userAgent.match(/firefox|fxios/i)) {
      browserName = "Firefox";
    } else if (userAgent.match(/safari/i)) {
      browserName = "Safari";
    } else if (userAgent.match(/opr\//i)) {
      browserName = "Opera";
    } else if (userAgent.match(/edg/i)) {
      browserName = "Edge";
    } else if (userAgent.match(/msie|trident/i)) {
      browserName = "IE";
    }
    
    return browserName;
  }



  async isDeviceRemembered() {
    console.log('RememberDevice: validation disabled by business decision');
    return { valid: false };
  }

  async rememberDevice(userId, email) {
    console.log('RememberDevice: Attempting to remember device (server-side)', { userId, email });
    console.log('[DEBUG] rememberDevice params - userId:', userId, 'email:', email);
    console.log('[DEBUG] userId type:', typeof userId, 'userId value:', userId);
    
    try {
      const fingerprint = await this.getDeviceFingerprint();
      const browserName = this.getBrowserName();
      const { default: apiProxy } = await import('@/services/apiProxy');
      
      const response = await apiProxy.call('device_remember', {
        action: 'create',
        fingerprint: fingerprint,
        email: email,
        browser: browserName
      });
      
      if (response.success) {
        console.log('RememberDevice: Device remembered successfully on server');
        // Set remember_device cookie to indicate device is registered
        document.cookie = `remember_device=true; path=/; max-age=${30 * 24 * 60 * 60}`; // 30 days
      } else {
        console.log('RememberDevice: Failed to remember device on server');
        throw new Error('Server registration failed');
      }
    } catch (error) {
      console.log('RememberDevice: Error remembering device', error);
      throw error;
    }
  }



  /**
   * Get client IP address using a public API
   * @returns {Promise<string>} Promise resolving to IP address or 'unknown'
   */
  async getClientIP() {
    try {
      const { apiProxy } = await import('@/services/apiProxy');
      const data = await apiProxy.call('get_client_ip', {});
      return data.ip || 'unknown';
    } catch (error) {
      return 'unknown';
    }
  } 



  /**
   * Forget the current device
   */
  async forgetDevice() {
    try {
      const { default: apiProxy } = await import('@/services/apiProxy');
      const fingerprint = await this.getDeviceFingerprint();
      
      await apiProxy.call('device_remember', {
        action: 'invalidate',
        fingerprint: fingerprint
      });
      
      console.log('RememberDevice: Device invalidated on server');
    } catch (error) {
      console.log('RememberDevice: Server invalidation failed', error);
    }
    
    // Remove remember_device cookie
    document.cookie = 'remember_device=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    this.fingerprintCache.value = null;
  }
}

export default new RememberDevice();
